/* import __COLOCATED_TEMPLATE__ from './edit-project-fees-modal.hbs'; */
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import LSGModalBase from "../lsg/lsg-modal-dialog-base";
import { closeModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponseWithModal, setErrors } from 'sh-www/utils/handles-errors';
import isNumber from 'is-number';
import ShPayment from "../../../models/sh-payment";
import ShInvoice from "../../../models/sh-invoice";
import Application from "../../../models/application";

/**
 * This is the component for the modal to change a project's price
 */
const EditProjectFeesModal = LSGModalBase.extend({
  /**
   * Register services
   */
  applicationCommandService : service('commands/application-commands'),
  shInvoiceCommandService : service('commands/sh-invoice-commands'),
  shPaymentCommandService : service('commands/sh-payment-commands'),

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    /**
     * @type {Application|ShInvoice|ShPayment|null}
     */
    relatedModel: null,

    /**
     * A callback with the new changed price after user confirms
     *
     * @type {Function}
     */
    onFeesUpdated: () => () => {
      throw new Error('define edit fees confirm')
    },

    /**
     * Should we allow Buyer fees to be edited?
     */
    showBuyerFees: true,

    /**
     * Should we allow Seller fees to be edited?
     */
    showSellerFees: true,
  },

  /*
   * @inheritDoc
   */
  stateDefaults: {
    _submitInProgress: false,

    buyerBaseFee: null,
    buyerFlatBaseFee: null,
    buyerExpenseFee: null,
    buyerAdvanceFee: null,
    buyerSelectFee: null,

    sellerBaseFee: null,
    sellerFlatBaseFee: null,
    sellerExpenseFee: null,
    sellerAdvanceFee: null,
    sellerSelectFee: null,

    isInviteeHire: null,
    isRepeatHire: null,
    isModified: null,
  },

  dimensionFields: {
    buyerBaseFee: 'buyer_base_fee',
    buyerFlatBaseFee: 'buyer_flat_base_fee',
    buyerExpenseFee: 'buyer_expense_fee',
    buyerAdvanceFee: 'buyer_advance_fee',
    buyerSelectFee: 'buyer_select_fee',
    sellerBaseFee: 'seller_base_fee',
    sellerFlatBaseFee: 'seller_flat_base_fee',
    sellerExpenseFee: 'seller_expense_fee',
    sellerAdvanceFee: 'seller_advance_fee',
    sellerSelectFee: 'seller_select_fee',
  },

  companionFields: {
    buyerBaseFee: 'buyerFlatBaseFee',
    buyerFlatBaseFee: 'buyerBaseFee',
    sellerBaseFee: 'sellerFlatBaseFee',
    sellerFlatBaseFee: 'sellerBaseFee',
  },

  _changeModelFeesText: computed(
    'model.relatedModel',
    function() {
      const relatedModel = this.get('model.relatedModel');
      if (relatedModel instanceof ShInvoice) {
        return 'Change Invoice Fees'
      } else if (relatedModel instanceof ShPayment) {
        return 'Change Payment Fees'
      }
      return 'Change Application Fees';
    }
  ),

  /**
   * Simplifies access to the application in the case that the model being edited is a billable
   *
   * @type {Application}
   */
  _application: computed(
    'model.relatedModel',
    function() {
      const relatedModel = this.get('model.relatedModel');

      if (relatedModel instanceof Application) {
        return relatedModel;
      }

      return relatedModel.get('application');
    }
  ),

  /**
   * @inheritDoc
   */
  _onInitializationComplete() {
    this.set('state._submitInProgress', false);
    this.set('errors', {});

    const feeStructure = this.get('model.relatedModel.feeStructure');

    this.set('state.buyerBaseFee',
      feeStructure.get('buyerFlatBaseFee') ? null : (feeStructure.get('buyerBaseFee') * 100).toFixed(1)
    );
    this.set('state.buyerFlatBaseFee', feeStructure.get('buyerFlatBaseFee'));
    this.set('state.buyerExpenseFee', (feeStructure.get('buyerExpenseFee') * 100).toFixed(1));
    this.set('state.buyerAdvanceFee', (feeStructure.get('buyerAdvanceFee') * 100).toFixed(1));
    this.set('state.buyerSelectFee',(feeStructure.get('buyerSelectFee') * 100).toFixed(1));

    this.set('state.sellerBaseFee',
      feeStructure.get('sellerFlatBaseFee') ? null : (feeStructure.get('sellerBaseFee') * 100).toFixed(1)
    );
    this.set('state.sellerFlatBaseFee', feeStructure.get('sellerFlatBaseFee'));
    this.set('state.sellerExpenseFee', (feeStructure.get('sellerExpenseFee') * 100).toFixed(1));
    this.set('state.sellerAdvanceFee', (feeStructure.get('sellerAdvanceFee') * 100).toFixed(1));
    this.set('state.sellerSelectFee', (feeStructure.get('sellerSelectFee') * 100).toFixed(1));

    this.set('state.isInviteeHire', feeStructure.get('isInviteeHire'));
    this.set('state.isRepeatHire', feeStructure.get('isRepeatHire'));
    this.set('state.isModified', feeStructure.get('isModified'));

    // this.set('_showBuyerFees', this.get('model.showBuyerFees') !== false);
    // this.set('_showSellerFees', this.get('model.showSellerFees') !== false);
  },

  formatPercent(value) {
    if (!isNumber(value)) {
      return null;
    }

    return (value * 100).toFixed(2);
  },

  validateFields() {
    this.set('errors', {});

    /** @type {Array<{ field: string, text: string }>} */
    const errors = [];

    const addError = (field, text) => errors.push({ field, text });

    Object.keys(this.dimensionFields).forEach((field) => {
      const value = this.get(`state.${field}`);

      const message = this.validateField(field, value);

      if (message) {
        addError(field, message);
      }
    });

    if (errors.length) {
      const errorsArr = errors.map(({ field }) => field);

      const errorsObj = errors.reduce((accum, { field, text }) => {
        accum[field] = text;
        return accum;
      }, { });

      setErrors(this, errorsObj, errorsArr, false, true);
    }
  },

  validateField(field, value) {
    if (value === null) {
      if (this.companionFields[field]) {
        if (this.get(`state.${this.companionFields[field]}`) === null) {
          return 'Must set one';
        }
        return null;
      }
    }

    if (!isNumber(value)) {
      return 'Must be a number';
    }

    const floatValue = parseFloat(value);

    if (field.includes('Flat')) {
      if (floatValue < 0.00) {
        return 'Must be positive';
      }
    } else {
      if (floatValue > 100.0 || floatValue < 0.00) {
        return 'Must be between 0 and 100';
      }
    }

    return null;
  },

  actions: {
    handleClose() {
      closeModal(this);
    },

    updateFees() {
      this.set('state._submitInProgress', true);

      var feeStructureJSON = {'fee_structure': {}};

      Object.entries(this.dimensionFields).forEach(([local, remote]) => {
        var value = this.get(`state.${local}`);

        // Translate percentages to decimals
        if (local.includes('Flat')) {
          if (value) {
            value = parseFloat(value).toFixed(2);
          }
          feeStructureJSON[remote] = value;
        } else {
          if (value !== null) {
            feeStructureJSON[remote] = (value / 100).toFixed(3);
          }
        }
      });

      // Call update
      var commandService;
      const relatedModel = this.get('model.relatedModel');
      if (relatedModel instanceof Application) {
        commandService = this.get('applicationCommandService');
      } else if (relatedModel instanceof ShInvoice) {
        commandService = this.get('shInvoiceCommandService');
      } else if (relatedModel instanceof ShPayment) {
        commandService = this.get('shPaymentCommandService');
      }

        commandService.updateFeeStructure(
        this.get('model.relatedModel'),
        feeStructureJSON
      ).then(() => {
        closeModal(this);
        this.get('model.onFeesUpdated')();
      }).catch(e => {
        handleErrorResponseWithModal(this, e);
      }).finally(() => {
        this.set('state._submitInProgress', false);
      });
    },

    /**
     * @param {string} field
     * @param {string} value
     */
    updateField: function(field, value) {
      // Clear mutually exclusive fields
      switch (field) {
        case 'buyerBaseFee':
          this.set('state.buyerFlatBaseFee', null);
          break;
        case 'buyerFlatBaseFee':
          this.set('state.buyerBaseFee', null);
          break;
        case 'sellerBaseFee':
          this.set('state.sellerFlatBaseFee', null);
          break;
        case 'sellerFlatBaseFee':
          this.set('state.sellerBaseFee', null);
          break;
      }

      this.set(`state.${field}`, value || null);

      this.validateFields();
    },

    validateFields: function() {
      this.validateFields();
    },

    /**
     * @param {JQuery.Event} e
     */
    onKeyDown(e) {
      if (e.key === 'Enter') {
        //don't allow enter to do anything on the text area 'enter' key press.
        e.preventDefault();
        e.stopPropagation();
      }
    },

    closeModal() {
      closeModal(this);
    },
  }
});

EditProjectFeesModal.reopenClass({
  FORM_FIELDS: [
    'client-exception'
  ]
});

export default EditProjectFeesModal;
